//@ts-check
import { connectRoutes } from "redux-first-router";
import type { Store } from "@reduxjs/toolkit";
import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { browserLanguage } from "./AppConstants";
import { routePaths } from "./routes";
import type { IAppStore } from "./utils/ReduxHooks";

import loginDataReducer from "./features/login/LoginDuck";
import configReducer from "./utils/Config/ConfigDuck";
import routeOptions from "./utils/RouteOptions";
import textData from "./utils/TextData";
import classMailReducer from "./features/classes/ClassItems/ClassMail/ClassMailDuck";
import classFilesReducer from "./features/classes/ClassItems/ClassFiles/ClassFilesDuck";
import classesReducer from "./features/classes/ClassesDuck";

import courseReducer from "./features/course/CourseDuck";
import confirmTestReducer from "./features/course/part/ConfirmTest/ConfirmTestDuck";
import docBoxReducer from "./features/General/DocBox/DocBoxDuck";
import drawingTestReducer from "./features/course/part/DrawingTest/DrawingTestDuck";
import errorsReducer from "./components/ErrorHandler/ErrorHandlerDuck";
import feedReducer from "./features/classes/ClassItems/Feed/FeedDuck";
import filetestReducer from "./features/course/part/FileTest/FileTestDuck";
import fileUploadReducer from "./components/FileUpload/FileUploadDuck";
import forumReducer from "./features/classes/ClassItems/Forum/ForumDuck";
import imagePickerReducer from "./components/ImagePicker/ImagePickerDuck";
import layoutReducer from "./features/layout/LayoutDuck";
import lectureReducer from "./features/course/part/Lectures/LectureDuck";
import mcTestReducer from "./features/course/part/McTest/McTestDuck";
import mqaTestReducer from "./features/course/part/MQATest/MQATestDuck";
import networkReconnectReducer from "./features/network/NetworkReconnectDuck";
import permissionsReducer from "./features/Permissions/PermissionDuck";
import singleMailReducer from "./features/General/SingleMail/SingleMailDuck";
import qaTestReducer from "./features/course/part/QATest/QATestDuck";

import textReducer from "./features/language/LangageDuck";
import timetableReducer from "./features/classes/ClassItems/TimeTable/TimeTableDuck";
import userReducer from "./features/User/UserDuck";
import searchReducer from "./features/search/SearchDuck";
import d3GraphReducer from "./ducks/d3Graph/D3GraphDuck";



export default function configureAppStore(clean: boolean = false): IAppStore {
    const {
        reducer: location,
        middleware: routerMiddleware,
        enhancer: routerEnhancer
    } = connectRoutes(routePaths(), routeOptions);

    const staticReducers = {
        classData: classesReducer,
        classFiles: classFilesReducer,
        classMail: classMailReducer,
        confirmTest: confirmTestReducer,
        course: courseReducer,
        config: configReducer,
        drawingTest: drawingTestReducer,
        docbox: docBoxReducer,
        d3graph: d3GraphReducer,
        errors: errorsReducer,
        feed: feedReducer,
        filetest: filetestReducer,
        fileUpload: fileUploadReducer,
        forum: forumReducer,
        imagepicker: imagePickerReducer,
        layout: layoutReducer,
        lecture: lectureReducer,
        location: location,
        login: loginDataReducer,
        mctest: mcTestReducer,
        mqatest: mqaTestReducer,
        networkReconnect: networkReconnectReducer,
        permissions: permissionsReducer,
        qatest: qaTestReducer,
        search: searchReducer,
        singleMail: singleMailReducer,
        text: textReducer,
        timetable: timetableReducer,
        user: userReducer,
    };

    let store: Store = null;

    if (!clean) {
        let text = {};
        let currentLang = {};
        if (browserLanguage) {
            const lang = "x" + browserLanguage.substr(0, 2).toLowerCase();
            text = textData.text[lang];
            currentLang = textData.languages.find(l => l.Short === lang);
        }
        else {
            text = textData.text.xen;  // English default
            currentLang = textData.languages[1];
        }

        const preloadedState = { text: { languages: textData.languages, text: text, currentLang: currentLang } };

        store = configureStore({
            reducer: staticReducers,
            middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false }).concat(routerMiddleware),

            // save({
            //     debounce: 1000,
            //     states: ["login"]
            // })
            preloadedState: preloadedState,
             enhancers: (getDefaultEnhancers) =>
                    getDefaultEnhancers().concat(routerEnhancer),
        });



    } else {
        store = configureStore({
            reducer: staticReducers,
            middleware: (getDefaultMiddleware) => getDefaultMiddleware({ serializableCheck: false })
        });
    }


    // Add a dictionary to keep track of the registered async reducers
    const storeWithAsyncReducers = store as IAppStore;
    storeWithAsyncReducers.asyncReducers = {};

    // Create an inject reducer function
    // This function adds the async reducer, and creates a new combined reducer
    storeWithAsyncReducers.injectReducer = (key, asyncReducer) => {
        storeWithAsyncReducers.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(storeWithAsyncReducers.asyncReducers, staticReducers));
    };

    return store as IAppStore;
}

function createReducer(asyncReducers, staticReducers) {
    return combineReducers({
        ...staticReducers,
        ...asyncReducers
    })
}

